import React, { useRef, useEffect, useMemo } from "react";
import * as d3 from "d3";

interface ChartData {
  title?: string;
  labels?: string[];
  datasets?: Array<{ values?: number[] }>;
  currency?: string;
  chart_type?: string;
}

interface HorizontalBarChartProps {
  chart: ChartData;
  currency?: string;
}

const formatAxisValue = (num: number, currency?: string) => {
  if (!currency) return String(Math.abs(num));
  const absNum = Math.abs(num);
  if (absNum >= 1_000_000) return `${(absNum / 1_000_000).toFixed(1)}M`;
  if (absNum >= 1_000) return `${(absNum / 1_000).toFixed(1)}K`;
  return String(absNum);
};

const formatBarLabel = (num: number, currency?: string) => {
  if (!currency) return String(num);
  const suffix = Math.abs(num) >= 1_000_000 ? "M" : Math.abs(num) >= 1_000 ? "K" : "";
  const absNum = Math.abs(num);
  const shortNum = absNum >= 1_000_000 ? (absNum / 1_000_000).toFixed(1) : absNum >= 1_000 ? (absNum / 1_000).toFixed(1) : String(absNum);
  return num < 0 ? `-${shortNum}${suffix}` : `${shortNum}${suffix}`;
};

const formatHover = (num: number, currency?: string) => {
  if (!currency) return String(num);
  const suffix = Math.abs(num) >= 1_000_000 ? "M" : Math.abs(num) >= 1_000 ? "K" : "";
  const absNum = Math.abs(num);
  const shortNum = absNum >= 1_000_000 ? (absNum / 1_000_000).toFixed(1) : absNum >= 1_000 ? (absNum / 1_000).toFixed(1) : String(absNum);
  const numStr = num < 0 ? `-${shortNum}${suffix}` : `${shortNum}${suffix}`;
  return `${numStr} ${currency}`;
};

const truncateLabel = (str: string, maxLen: number) => {
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen - 2) + "…";
};

export function HorizontalBarChart({ chart, currency }: HorizontalBarChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const data = useMemo(() => {
    if (!chart?.labels) return [];
    return chart.labels.map((label: string, i: number) => ({
      name: label,
      value: chart.datasets?.[0]?.values?.[i] || 0,
    }));
  }, [chart]);

  const chartCurrency = currency || chart?.currency || "AED";
  const maxAbsValue = useMemo(() => Math.max(...data.map(d => Math.abs(d.value))), [data]);
  const hasNegative = useMemo(() => data.some(d => d.value < 0), [data]);
  
  const yAxisWidth = hasNegative ? 100 : 90;
  const rightPadding = 90;
  const chartHeight = Math.max(data.length * 42 + 70, 230);
  const margin = { top: 15, right: rightPadding, bottom: 20, left: yAxisWidth };

  useEffect(() => {
    if (!svgRef.current || !data.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const containerWidth = svgRef.current.parentElement?.clientWidth || 300;
    const width = Math.max(containerWidth - 20, 200);
    const height = chartHeight;
    const barWidth = width - margin.left - margin.right;
    const barHeight = height - margin.top - margin.bottom;

    const xMax = maxAbsValue * 1.15;
    const x = d3.scaleLinear().domain([hasNegative ? -xMax : 0, xMax]).range([0, barWidth]);

    const y = d3.scaleBand()
      .domain(data.map((d) => d.name))
      .range([0, barHeight])
      .padding(0.35);

    const chartGroup = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    if (hasNegative) {
      chartGroup
        .append("line")
        .attr("x1", x(0))
        .attr("x2", x(0))
        .attr("y1", 0)
        .attr("y2", barHeight)
        .attr("stroke", "#9CA3AF")
        .attr("stroke-width", 1);
    }

    const xTicks = x.ticks(5).filter(t => t !== 0);
    chartGroup
      .selectAll(".grid-line")
      .data(xTicks)
      .enter()
      .append("line")
      .attr("x1", d => x(d))
      .attr("x2", d => x(d))
      .attr("y1", 0)
      .attr("y2", barHeight)
      .attr("stroke", "#E5E7EB")
      .attr("stroke-dasharray", "3,3");

    chartGroup
      .selectAll(".x-tick")
      .data(xTicks)
      .enter()
      .append("text")
      .attr("x", d => x(d))
      .attr("y", barHeight + 16)
      .attr("text-anchor", "middle")
      .attr("font-size", "10px")
      .attr("fill", "#6B7280")
      .text(d => formatAxisValue(d, chartCurrency));

    chartGroup
      .selectAll(".bar")
      .data(data)
      .enter()
      .append("rect")
      .attr("x", d => d.value >= 0 ? x(0) : x(d.value))
      .attr("y", d => y(d.name) || 0)
      .attr("width", d => Math.abs(x(d.value) - x(0)))
      .attr("height", y.bandwidth())
      .attr("fill", d => d.value >= 0 ? "#4AC08C" : "#E85D3A")
      .attr("rx", 3)
      .style("cursor", "pointer")
      .on("mouseenter", function (event, d) {
        d3.select(this).attr("fill", d => d.value >= 0 ? "#3AA87A" : "#D14529");
        if (tooltipRef.current) {
          tooltipRef.current.style.opacity = "1";
          tooltipRef.current.innerHTML = `<strong>${d.name}</strong><br/>${formatHover(d.value, chartCurrency)}`;
          tooltipRef.current.style.left = `${event.offsetX + 15}px`;
          tooltipRef.current.style.top = `${event.offsetY - 10}px`;
        }
      })
      .on("mouseleave", function (event, d) {
        d3.select(this).attr("fill", d => d.value >= 0 ? "#4AC08C" : "#E85D3A");
        if (tooltipRef.current) {
          tooltipRef.current.style.opacity = "0";
        }
      });

    chartGroup
      .selectAll(".value-label")
      .data(data)
      .enter()
      .append("text")
      .attr("x", d => {
        if (d.value >= 0) {
          return x(d.value) + 6;
        } else {
          return x(0) + 6;
        }
      })
      .attr("y", d => (y(d.name) || 0) + y.bandwidth() / 2)
      .attr("dy", "0.35em")
      .attr("text-anchor", "start")
      .attr("font-size", "9px")
      .attr("font-weight", "600")
      .attr("fill", "#374151")
      .text(d => formatBarLabel(d.value, chartCurrency));

    const yAxis = chartGroup
      .append("g")
      .attr("transform", `translate(0,0)`)
      .call(d3.axisLeft(y).tickSize(0).tickFormat(d => truncateLabel(String(d), 15)));

    yAxis.selectAll("text").attr("font-size", "10px").attr("fill", "#6B7280");
    yAxis.selectAll(".domain").remove();
  }, [data, chartCurrency, maxAbsValue, hasNegative, rightPadding, yAxisWidth, chartHeight, margin]);

  return (
    <div className="ai-summary-panel__card" ref={containerRef}>
      <p className="ai-summary-panel__card-title">{chart?.title || ""}</p>
      <div style={{ position: "relative", overflow: "visible", height: chartHeight, width: "100%" }}>
        <svg ref={svgRef} width="100%" height={chartHeight} style={{ display: "block" }}></svg>
        <div
          ref={tooltipRef}
          style={{
            position: "absolute",
            backgroundColor: "white",
            padding: "8px 12px",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            fontSize: "11px",
            color: "#374151",
            opacity: 0,
            transition: "opacity 0.15s",
            pointerEvents: "none",
            zIndex: 10,
            border: "1px solid #E5E7EB",
            lineHeight: 1.4,
          }}
        />
      </div>
    </div>
  );
}

export default HorizontalBarChart;